import { supabase } from '../../config/supabase';

// ─────────────────────────────────────────────────────────────
// TABLE SCHEMA ASSUMED:
//
// production_schedule (new table)
//   id            uuid PK default gen_random_uuid()
//   job_id        uuid FK → jobs.id
//   quote_type    text  (roof | wall | floor | timber)
//   company_id    uuid FK → companies.id
//   col_a         text  nullable
//   col_b         text  nullable
//   col_c         text  nullable
//   col_d         text  nullable
//   col_e         text  nullable
//   col_f         text  nullable
//   delivery_time text  nullable   (auto-populated from job, editable)
//   scheduled_date date nullable
//   created_at    timestamptz default now()
//   updated_at    timestamptz default now()
//   sort_order    integer default 0
//
// jobs table (existing) — no changes needed
// job_quotes table (existing) — no changes needed
// ─────────────────────────────────────────────────────────────

export const getProductionSortKey = ({
    quoteTypeFilter,
    jobTypeFilter,
    urgentFilter,
    hideCompleted,
}) => {

    return JSON.stringify({
        quote: quoteTypeFilter || null,
        jobType: jobTypeFilter || 'all',
        urgent: urgentFilter || 'all',
        hideCompleted: !!hideCompleted,
    });

};

const fetchAll = async (baseQuery, pageSize = 1000) => {
    let allRows = [];
    let from = 0;
    let to = pageSize - 1;

    while (true) {
        const { data, error } = await baseQuery.range(from, to);

        if (error) throw error;

        const rows = data || [];
        allRows = [...allRows, ...rows];

        if (rows.length < pageSize) break;

        from += pageSize;
        to += pageSize;
    }

    return allRows;
};



// ─── Fetch production schedule rows (for the virtual table) ──────────────────
export const getProductionScheduleRows = async (companyId, filters = {}) => {
    try {
        const {
            sortBy = 'sort_order',
            jobTypeFilter = 'all',
            urgentFilter = 'all',
            quoteTypeFilter = null, // 'roof'|'wall'|'floor'|'timber'|null
        } = filters;

        let query = supabase
            .from('job_production')
            .select(`
                *,
                jobs (
                    id,
                    unique_id,
                    customer_name,
                    job_type,
                    main_status,
                    is_cancelled,
                    urgent_quote,
                    urgent_design,
                    booking_delivery_date,
                    created_at,
                    uid_prefix,
                    uid_number,
                   job_quotes ( id, quote_type, is_required, delivery_time_slot, child_unique_id, layout_only, expected_delivery_date )
                )
            `)
            .eq('company_id', companyId);

        // quote type filter
        if (quoteTypeFilter) {
            query = query.eq('quote_type', quoteTypeFilter);
        }

        // job_type filter via jobs relation
        if (jobTypeFilter && jobTypeFilter !== 'all') {
            if (jobTypeFilter === 'design_only') {
                query = query.eq('jobs.job_type', 'Design');
            } else if (jobTypeFilter === 'estimation_only') {
                query = query.eq('jobs.job_type', 'Estimation');
            } else if (jobTypeFilter === 'design_estimation_only') {
                query = query.eq('jobs.job_type', 'Design & Estimation');
            } else if (jobTypeFilter === 'timber_only') {
                query = query.eq('jobs.job_type', 'Timber');
            }
        }

        // urgent filter
        if (urgentFilter && urgentFilter !== 'all') {
            if (urgentFilter === 'urgent_quote') {
                query = query.eq('jobs.urgent_quote', true);
            } else if (urgentFilter === 'urgent_design') {
                query = query.eq('jobs.urgent_design', true);
            }
        }

        // sorting
        if (sortBy === 'newest') {
            query = query.order('created_at', { ascending: false });
        } else if (sortBy === 'oldest') {
            query = query.order('created_at', { ascending: true });
        } else {
            query = query.order('sort_order', { ascending: true }).order('created_at', { ascending: false });
        }

        const data = await fetchAll(query);

        const rows = (data || []).filter(row => !row.jobs?.is_cancelled).reduce((acc, row) => {
            // Match by UUID when available (new-system jobs); fall back to quote_type for old rows
            const matchedQuote = (row.jobs?.job_quotes || []).find(q =>
                row.quote_id ? q.id === row.quote_id : q.quote_type === row.quote_type
            );
            // Skip layout-only departments — they should not appear in production
            if (matchedQuote?.is_required === true && matchedQuote?.layout_only !== true) {
                acc.push({
                    id: row.id,
                    jobId: row.job_id,
                    quoteId: row.quote_id || null,
                    quoteType: row.quote_type,
                    quoteDeliveryDate: matchedQuote?.expected_delivery_date || row?.expected_delivery_date || null,
                    childUniqueId: matchedQuote?.child_unique_id || null,
                    companyId: row.company_id,
                    colA: row.col_a || '',
                    colB: row.col_b || '',
                    colC: row.col_c || '',
                    colD: row.col_d || '',
                    colE: row.col_e || '',
                    colF: row.col_f || '',
                    deliveryTime: row.delivery_time || matchedQuote?.delivery_time_slot || '',
                    scheduledDate: row.scheduled_date || null,
                    sortOrder: row.sort_order || 0,
                    sortOrderVariants: row.sort_order_variants || {},
                    createdAt: row.created_at,
                    updatedAt: row.updated_at,
                    // production phase fields from job_production
                    phase: row.phase || 'not_started',
                    isCompleted: row.is_completed || false,
                    isStartedCutting: row.is_started_cutting || false,
                    isStartedProduction: row.is_started_production || false,
                    startCuttingAt: row.start_cutting_at || null,
                    endCuttingAt: row.end_cutting_at || null,
                    startProductionAt: row.start_production_at || null,
                    endProductionAt: row.end_production_at || null,
                    userId: row.user_id || null,
                    userName: row.name || null,
                    // denorm from jobs join
                    uniqueId: row.jobs?.unique_id || '',
                    customerName: row.jobs?.customer_name || '',
                    jobType: row.jobs?.job_type || '',
                    mainStatus: row.jobs?.main_status || '',
                    urgentQuote: row.jobs?.urgent_quote || false,
                    urgentDesign: row.jobs?.urgent_design || false,
                    bookingDeliveryDate: row.jobs?.booking_delivery_date || null,
                    uidPrefix: row.jobs?.uid_prefix || '',
                    uidNumber: row.jobs?.uid_number || 0,
                });
            }
            return acc;
        }, []);

        const sortKey = getProductionSortKey(filters);

        rows.sort((a, b) => {

            const aVariant = a.sortOrderVariants?.[sortKey];
            const bVariant = b.sortOrderVariants?.[sortKey];


            if (
                aVariant !== undefined &&
                bVariant !== undefined
            ) {
                return aVariant - bVariant;
            }


            return a.sortOrder - b.sortOrder;

        });


        return { data: rows };
    } catch (err) {
        console.error('getProductionScheduleRows error:', err);
        throw err;
    }
};

// ─── Fetch "Production Ready" jobs (design_status = completed, not yet in production_schedule) ──
export const getProductionReadyJobs = async (companyId, filters = {}) => {
    try {
        const {
            sortBy = 'newest',
            jobTypeFilter = 'all',
            urgentFilter = 'all',
            quoteTypeFilter = null,
            searchQuery = '',
        } = filters;

        // Get job IDs already in production_schedule so we can exclude them
        const scheduled = await fetchAll(
            supabase
                .from('job_production')
                .select('job_id, quote_type, quote_id')
                .eq('company_id', companyId)
        );

        // Build exclusion set using quote UUID — using job_id|quote_type would wrongly exclude
        // all children of the same type when only one is scheduled (e.g. TRG3R-2 excluded
        // because TRG3R-1 is already in production).
        const scheduledQuoteIds = new Set(
            (scheduled || []).map(r => r.quote_id).filter(Boolean)
        );

        const scheduledLegacyKeys = new Set(
            (scheduled || [])
                .filter(r => !r.quote_id && r.job_id && r.quote_type)
                .map(r => `${r.job_id}|${r.quote_type}`)
        );

        // Fetch jobs with completed design status
        let query = supabase
            .from('jobs')
            .select(`
                id,
                unique_id,
                customer_name,
                job_type,
                main_status,
                is_cancelled,
                urgent_quote,
                urgent_design,
                booking_delivery_date,
                created_at,
                uid_prefix,
                uid_number,
                job_quotes (
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    production_status,
                    expected_delivery_date,
                    delivery_count,
                    delivery_time_slot,
                    child_unique_id,
                    layout_only,
                    sent_production
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .or('is_cancelled.is.null,is_cancelled.eq.false')
            .in('job_type', ['Design', 'Design & Estimation', 'Timber'])
            .eq('job_quotes.is_required', true)
            .or('design_status.eq.completed,sent_production.eq.true', { foreignTable: 'job_quotes' })
            .or('delivery_count.is.null,delivery_count.eq.0', { foreignTable: 'job_quotes' });

        // job type filter
        if (jobTypeFilter && jobTypeFilter !== 'all') {
            if (jobTypeFilter === 'design_only') query = query.eq('job_type', 'Design');
            else if (jobTypeFilter === 'estimation_only') query = query.eq('job_type', 'Estimation');
            else if (jobTypeFilter === 'design_estimation_only') query = query.eq('job_type', 'Design & Estimation');
            else if (jobTypeFilter === 'timber_only') query = query.eq('job_type', 'Timber');
        }

        // urgent filter
        if (urgentFilter && urgentFilter !== 'all') {
            if (urgentFilter === 'urgent_quote') query = query.eq('urgent_quote', true);
            else if (urgentFilter === 'urgent_design') query = query.eq('urgent_design', true);
        }

        // search
        if (searchQuery && searchQuery.trim()) {
            const q = searchQuery.trim();
            query = query.or(`unique_id.ilike.%${q}%,customer_name.ilike.%${q}%`);
        }

        if (quoteTypeFilter) {
            query = query.eq('job_quotes.quote_type', quoteTypeFilter);
        }

        // sorting
        if (sortBy === 'oldest') query = query.order('created_at', { ascending: true });
        else if (sortBy === 'alphabetical') query = query.order('uid_prefix', { ascending: true }).order('uid_number', { ascending: true });
        else if (sortBy === 'reverse_alphabetical') query = query.order('uid_prefix', { ascending: false }).order('uid_number', { ascending: false });
        else if (sortBy === 'priority') {
            query = query
                .order('urgent_quote', { ascending: false, nullsFirst: false })
                .order('urgent_design', { ascending: false, nullsFirst: false })
                .order('created_at', { ascending: true });
        } else {
            query = query.order('created_at', { ascending: false });
        }

        const data = await fetchAll(query);

        // Expand per required quote type and filter out already-scheduled ones
        const readyJobs = [];
        (data || []).forEach(job => {
            const initialQuotes = (job.job_quotes || []).filter(q =>
                q.is_required &&
                (q.design_status === 'completed' || q.sent_production === true) &&
                (q.delivery_count === null || q.delivery_count === 0)
            );

            initialQuotes.forEach(quote => {
                // Skip layout-only departments — they should not appear in production ready
                if (quote.layout_only) return;

                const alreadyScheduled =
                    scheduledQuoteIds.has(quote.id) ||
                    scheduledLegacyKeys.has(`${job.id}|${quote.quote_type}`);

                if (!alreadyScheduled) {
                    readyJobs.push({
                        jobId: job.id,
                        uniqueId: job.unique_id,
                        childUniqueId: quote?.child_unique_id || null,
                        customerName: job.customer_name,
                        jobType: job.job_type,
                        mainStatus: job.main_status,
                        urgentQuote: job.urgent_quote,
                        urgentDesign: job.urgent_design,
                        bookingDeliveryDate: job.booking_delivery_date,
                        createdAt: job.created_at,
                        quoteType: quote.quote_type,
                        quoteId: quote.id,
                        productionStatus: quote.production_status,
                        expectedDeliveryDate: quote.expected_delivery_date,
                        deliveryTimeSlot: quote.delivery_time_slot || '',
                    });
                }
            });
        });

        return { data: readyJobs };
    } catch (err) {
        console.error('getProductionReadyJobs error:', err);
        throw err;
    }
};

// ─── Add a job to the production schedule (drag-drop creates this row) ────────
export const addToProductionSchedule = async (companyId, { jobId, quoteType, jobQuoteId, scheduledDate }, user) => {
    try {
        // Check for duplicate (per quote UUID, not per type — multiple children can share a type)
        const { data: existing } = await supabase
            .from('job_production')
            .select('id')
            .eq('company_id', companyId)
            .eq('job_id', jobId)
            .eq('quote_id', jobQuoteId)
            .maybeSingle();

        if (existing) {
            throw new Error(`This job/quote is already in the production schedule.`);
        }

        const { data: jobData, error: jobError } = await supabase
            .from('jobs')
            .select('is_cancelled')
            .eq('id', jobId)
            .eq('company_id', companyId)
            .maybeSingle();

        if (jobError) throw jobError;

        if (jobData?.is_cancelled) {
            throw new Error('This job is cancelled and cannot be added to production schedule.');
        }

        // Fetch delivery_time_slot and child_unique_id from job_quotes
        const { data: quoteData } = await supabase
            .from('job_quotes')
            .select('delivery_time_slot, child_unique_id')
            .eq('id', jobQuoteId)
            .maybeSingle();

        const deliveryTime = quoteData?.delivery_time_slot || null;
        const childUniqueId = quoteData?.child_unique_id || null;

        // Get the next sort_order
        const { data: lastRow } = await supabase
            .from('job_production')
            .select('sort_order')
            .eq('company_id', companyId)
            .order('sort_order', { ascending: false })
            .limit(1)
            .maybeSingle();

        const nextOrder = (lastRow?.sort_order ?? -1) + 1;

        const { data, error } = await supabase
            .from('job_production')
            .insert({
                company_id: companyId,
                job_id: jobId,
                quote_type: quoteType,
                ...(scheduledDate ? { scheduled_date: scheduledDate } : {}),
                sort_order: nextOrder,
                user_id: user?.id,
                name: user?.username,
                quote_id: jobQuoteId,
            })
            .select(`
                *,
                jobs (
                    id,
                    unique_id,
                    customer_name,
                    job_type,
                    main_status,
                    urgent_quote,
                    urgent_design,
                    booking_delivery_date,
                    uid_prefix,
                    uid_number
                )
            `)
            .single();

        if (error) throw error;

        return {
            id: data.id,
            jobId: data.job_id,
            quoteId: data.quote_id || null,
            quoteType: data.quote_type,
            childUniqueId,
            companyId: data.company_id,
            colA: data.col_a || '',
            colB: data.col_b || '',
            colC: data.col_c || '',
            colD: data.col_d || '',
            colE: data.col_e || '',
            colF: data.col_f || '',
            deliveryTime: deliveryTime || '',
            scheduledDate: data.scheduled_date || null,
            sortOrder: data.sort_order || 0,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            phase: data.phase || 'not_started',
            isCompleted: data.is_completed || false,
            userId: data.user_id || null,
            userName: data.name || null,
            uniqueId: data.jobs?.unique_id || '',
            customerName: data.jobs?.customer_name || '',
            jobType: data.jobs?.job_type || '',
            mainStatus: data.jobs?.main_status || '',
            urgentQuote: data.jobs?.urgent_quote || false,
            urgentDesign: data.jobs?.urgent_design || false,
            bookingDeliveryDate: data.jobs?.booking_delivery_date || null,
            uidPrefix: data.jobs?.uid_prefix || '',
            uidNumber: data.jobs?.uid_number || 0,
        };
    } catch (err) {
        console.error('addToProductionSchedule error:', err);
        throw err;
    }
};

// ─── Update production phase (start/end cutting / production) ────────────────
export const updateProductionPhase = async (rowId, action) => {
    const now = new Date().toISOString();
    let update = {};

    switch (action) {
        case 'start_cutting':
            update = { phase: 'in_cutting', is_started_cutting: true, start_cutting_at: now };
            break;
        case 'end_cutting':
            update = { phase: 'cutting_ended', end_cutting_at: now };
            break;
        case 'start_production':
            update = { phase: 'in_production', is_started_production: true, start_production_at: now };
            break;
        case 'end_production':
            update = { phase: 'completed', is_completed: true, end_production_at: now };
            break;
        default:
            throw new Error(`Unknown production action: ${action}`);
    }

    try {
        const { data, error } = await supabase
            .from('job_production')
            .update({ ...update, updated_at: now })
            .eq('id', rowId)
            .select()
            .maybeSingle();
        if (error) throw error;
        if (!data) throw new Error(`Production row not found: ${rowId}`);
        return data;
    } catch (err) {
        console.error('updateProductionPhase error:', err);
        throw err;
    }
};

// ─── Update a single cell in the production schedule ─────────────────────────
// column: 'col_a' | 'col_b' | 'col_c' | 'col_d' | 'col_e' | 'col_f' | 'scheduled_date' | 'delivery_time'
export const updateProductionScheduleCell = async (rowId, column, value) => {
    const allowed = ['col_a', 'col_b', 'col_c', 'col_d', 'col_e', 'col_f', 'scheduled_date'];
    if (!allowed.includes(column)) throw new Error(`Invalid column: ${column}`);

    try {
        const { data, error } = await supabase
            .from('job_production')
            .update({ [column]: value || null, updated_at: new Date().toISOString() })
            .eq('id', rowId)
            .select()
            .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error(`Production row not found: ${rowId}`);
        return data;
    } catch (err) {
        console.error('updateProductionScheduleCell error:', err);
        throw err;
    }
};

// ─── Remove a row from the production schedule ────────────────────────────────
export const removeFromProductionSchedule = async (rowId) => {
    try {
        const { error } = await supabase
            .from('job_production')
            .delete()
            .eq('id', rowId);

        if (error) throw error;
        return { success: true };
    } catch (err) {
        console.error('removeFromProductionSchedule error:', err);
        throw err;
    }
};

// ─── Update sort order (drag-and-drop row reordering in table) ────────────────
// export const updateProductionScheduleSortOrder = async (rows) => {
//     try {
//         const changedRows = rows.filter(r => r.sort_order !== r.current_sort_order);

//         for (const r of changedRows) {
//             const { error } = await supabase
//                 .from('job_production')
//                 .update({ sort_order: r.sort_order })
//                 .eq('id', r.id);

//             if (error) throw error;
//         }

//         return { success: true };
//     } catch (err) {
//         console.error('updateProductionScheduleSortOrder error:', err);
//         throw err;
//     }
// };

export const updateProductionScheduleSortOrder = async (updates) => {
    try {
        if (!updates || updates.length === 0) {
            return { success: true };
        }

        const { error } = await supabase.rpc('update_job_production_sort_orders', {
            p_updates: updates.map(u => ({
                id: u.id,
                sort_order: u.sort_order,
            })),
        });

        if (error) throw error;

        return { success: true };
    } catch (err) {
        console.error('updateProductionScheduleSortOrder error:', err);
        throw err;
    }
};

export const updateProductionScheduleVariantOrder = async (
    companyId,
    filterKey,
    rows
) => {

    // Only send rows whose position under this filter actually changed.
    // NOTE: this deliberately uses .update() (never .upsert()) — job_production
    // has NOT NULL columns (job_id, quote_type) with no default, and Postgres'
    // INSERT ... ON CONFLICT DO UPDATE builds the candidate insert row (and so
    // checks NOT NULL constraints) before conflict resolution even runs. A
    // partial-column upsert therefore fails on existing rows too. .update()
    // can never take an insert path, so it can't hit that constraint.
    const updates = rows.reduce((acc, row, index) => {
        const existingVariants = row.sortOrderVariants || {};
        if (existingVariants[filterKey] === index) return acc;

        acc.push({
            id: row.id,
            sort_order_variants: { ...existingVariants, [filterKey]: index },
        });
        return acc;
    }, []);

    if (updates.length === 0) return { success: true };

    // Run concurrently (not one sequential await per row) — the sequential
    // version was the source of the original request-storm/slow-save bug.
    const results = await Promise.all(
        updates.map(u =>
            supabase
                .from('job_production')
                .update({ sort_order_variants: u.sort_order_variants })
                .eq('id', u.id)
                .eq('company_id', companyId)
        )
    );

    const firstError = results.find(r => r.error)?.error;
    if (firstError) throw firstError;

    return { success: true };
};

// ─── Real-time subscriptions ──────────────────────────────────────────────────
export const subscribeToProductionSchedule = (companyId, callback) => {
    const channel = supabase
        .channel(`production-schedule-${companyId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_production',
                filter: `company_id=eq.${companyId}`,
            },
            (payload) => callback({ table: 'job_production', ...payload })
        )
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`,
            },
            (payload) => callback({ table: 'jobs', ...payload })
        )
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'job_quotes',
            },
            (payload) => callback({ table: 'job_quotes', ...payload })
        )
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'job_action_logs',
                filter: `company_id=eq.${companyId}`,
            },
            (payload) => callback({ table: 'job_action_logs', ...payload })
        )
        .subscribe();

    return channel;
};

export const unsubscribeFromProductionSchedule = async (channel) => {
    if (channel) await supabase.removeChannel(channel);
};

// ─── Fetch available users for filter dropdowns ───────────────────────────────
export const getProductionScheduleUsers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, role')
            .eq('company_id', companyId)
            .in('role', ['designer', 'estimator', 'admin'])
            .eq('status', 'active')
            .order('username', { ascending: true });

        if (error) throw error;

        return (data || []).map(u => ({
            userId: u.id,
            username: u.username || u.full_name,
            role: u.role,
        }));
    } catch (err) {
        console.error('getProductionScheduleUsers error:', err);
        throw err;
    }
};
